import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { UPLOADED_DOC_TYPES } from '@constants';
import { logger, logNetRequest, sleep } from '@utils/usefulFuncs';
import { sendMultipartApiRequest } from '@services/api';
import { logError } from '@utils/sentry';
import { TrackingEvent } from '@classes/TrackingEvent';
import { uploadFilesActions } from '@constants/actionTypes';

export const FILE_UPLOAD_STATUS = {
  uploading: 'up',
  failed: 'failed',
  complete: 'complete',
};

export const useUploadFormFiles = () => {
  const state = useSelector((state) => state.uploadFiles);
  return state.files;
};

const logUploadFile = (file) => {
  TrackingEvent.create('Uploaded Digital Analyst Content', {
    'File Type': file.type,
  });
};

const logUploadError = (response) => {
  logError(new Error('Error uploading document'), {
    error: response?.status,
    message: response?.data?.error,
    component: 'useUploadFormFiles',
  });
};

export const useFileUploader = (reupload) => {
  const dispatch = useDispatch();
  const tableExtractionFileTypes = ['pdf', 'doc', 'docx'];

  const uploadSingleFile = async (file, index) => {
    let formData = new FormData();
    const {
      type,
      name,
      speakerTag,
      company,
      docType,
      docId,
      extract_formula_dependencies,
      generate_template_spec,
      do_table_extraction,
      do_vision_extraction,
      table_extraction_tolerance,
    } = file;
    const ticker = company?.symbol || null;
    const companyTagId = company?.id || company?.doc_tag_id || null;

    logger('uploading', file, companyTagId);

    const tickerNormalized = ticker?.length === 0 ? null : ticker;
    if (tickerNormalized) {
      formData.append('ticker', tickerNormalized);
    }
    formData.append('name', name);
    if (companyTagId) {
      formData.append('doc_tag_id', companyTagId);
    }
    if (docId) {
      formData.append('doc_id', docId);
    }
    if (extract_formula_dependencies) {
      formData.append('extract_formula_dependencies', extract_formula_dependencies);
    }
    if (generate_template_spec) {
      formData.append('generate_template_spec', generate_template_spec);
    }
    if (tableExtractionFileTypes.includes(type)) {
      if (isNaN(table_extraction_tolerance)) {
        formData.append('use_adaptive_table_extraction', true);
      } else {
        formData.append('table_extraction_tolerance', table_extraction_tolerance);
      }
      if (do_table_extraction) {
        formData.append('do_table_extraction', do_table_extraction);
      }
      if (do_vision_extraction) {
        formData.append('do_vision_extraction', do_vision_extraction);
      }
    }
    if (speakerTag && speakerTag.length > 0) {
      formData.append('speaker', speakerTag);
    }
    console.log('file type detected: ' + type);
    if (docType !== null && docType !== '') {
      const formatDocType = (dt) => {
        if (UPLOADED_DOC_TYPES.includes(dt)) {
          return 'Uploaded ' + dt;
        }
        return dt;
      };
      formData.append('doc_type', formatDocType(docType));
    }

    let URL = `v1/genai_process_user_${type}`;
    if (['xls', 'xlsx', 'xlsm', 'xlsb'].includes(type)) {
      URL = 'v1/genai_process_user_xls';
    }
    if (['pptx', 'ppt', 'doc', 'docx'].includes(type)) {
      URL = 'v1/genai_process_user_ms_docs';
    }
    if (type === 'csv') {
      URL = 'v1/genai/upload/csv';
    }
    if (type === 'txt') {
      URL = 'v1/genai/upload/txt';
    }
    formData.append(type, file.file);
    if (reupload) {
      URL = 'v1/genai_process_user_doc_reupload';
    }

    dispatch({
      type: uploadFilesActions.updateFiles,
      payload: [
        {
          prevName: name,
          changes: {
            status: FILE_UPLOAD_STATUS.uploading,
            error: null,
          },
        },
      ],
    });
    await sleep(100 * index + 1);
    logger(URL, formData);
    logUploadFile(file);

    const response = await sendMultipartApiRequest('post', URL, formData);
    logNetRequest(response?.status, response?.data, response?.error);
    response?.status !== 200 && logUploadError(response);

    const wasSuccess = response?.status === 200;
    const errorMessage = wasSuccess
      ? null
      : response?.status === 413
      ? 'File is too large'
      : response?.data?.error || 'Something went wrong, please try again.';
    dispatch({
      type: uploadFilesActions.updateFiles,
      payload: [
        {
          prevName: name,
          changes: {
            status: wasSuccess ? FILE_UPLOAD_STATUS.complete : FILE_UPLOAD_STATUS.failed,
            error: errorMessage,
            id: response?.data?.doc_id || null,
          },
        },
      ],
    });
    return wasSuccess;
  };

  const uploadFiles = React.useCallback((fileOrFiles) => {
    if (Array.isArray(fileOrFiles)) {
      return Promise.all(
        fileOrFiles.map((file, index) => {
          return uploadSingleFile(file, index);
        })
      );
    } else {
      return uploadSingleFile(fileOrFiles, 0);
    }
  }, []);

  return uploadFiles;
};
